/** Empty state component for lists and tables with no matching results. */
'use client';

// Admin's empty-state dialects — a skin over the shared @auctogon/ui EmptyState
// (BL-1245, ADR 030 § 3b Canon merge; admin's implementation was the canonical
// base the shared primitive was built from).
//
// Structure lives in the shared primitive; this file binds admin's TWO
// appearances and adapts admin's structured `action` object into the right
// button for each. It is longer than a 2-5 line passthrough because it does
// both of those jobs — but it re-derives no structure, which is what
// sharing.md § 1's wrapper contract actually caps.
//
// The prop signature is unchanged from the pre-merge component (including the
// `action` OBJECT and the `variant` switch), so all 26 admin call sites migrate
// with no edit.

import { Inbox } from 'lucide-react';
import { clsx } from 'clsx';
import { EmptyState as UiEmptyState, type EmptyStateDialect } from '@auctogon/ui/web';
import Button from './Button';

/**
 * Which page shell the empty state renders inside.
 *
 * `classic` is the Tailwind dashboard chrome (customers, invoices, most detail
 * pages). `v2` is the settings-v2 shell (`.s-v2` — auctions list, locations,
 * settings), whose visual language is the CSS-variable `.ui-*` family in
 * settings-v2.css; a Tailwind block dropped in there reads as foreign chrome.
 *
 * One component, two dialects — not a fork. Before BL-916 the `.s-v2` surfaces
 * either hand-rolled their own `.ui-empty` block (LocationsTable) or fell back
 * to a bare `emptyText` string with no icon at all (auctions list).
 */
export type EmptyStateVariant = 'classic' | 'v2' | 'v2-inline';

/**
 * The classic Tailwind dashboard appearance.
 *
 * No `glyph` slot on purpose: classic puts the icon's size and color directly on
 * the icon with no wrapper, and the shared core renders it bare when the slot is
 * absent. No `action` slot either — classic's spacing rides on the Button's own
 * `mt-4`.
 */
const ADMIN_CLASSIC_DIALECT: EmptyStateDialect = {
    root: 'flex flex-col items-center justify-center py-12 px-4 text-center',
    icon: 'h-12 w-12 text-gray-300 mb-3 shrink-0',
    title: 'text-gray-500 font-medium max-w-xs',
    // BL-2503: the message carries the No access line, so it meets the text floor.
    //   gray-500 on white ........ 4.83:1  text ✓ (needs 4.5; gray-400 was 2.54:1)
    //   gray-500 on gray-50 ...... 4.63:1  text ✓
    message: 'text-gray-500 text-body mt-1 max-w-sm',
};

/**
 * The settings-v2 appearance — the `.ui-empty` class family from settings-v2.css.
 *
 * `titleAs` / `messageAs` are `div` because that is what the `.ui-empty__title`
 * and `.ui-empty__body` rules were written against; a `<p>` would inherit UA
 * margins those rules don't reset.
 *
 * The icon is 32px at stroke 1.5 (BUG-1258). `.ui-empty__glyph` was written for
 * a `✦` character and carried `font-size: var(--type-display)` — inert against an
 * SVG, so the icon actually rendered at 24px. That rule is gone and the size it
 * intended lives here instead, where it can reach the element. The lighter
 * stroke keeps the larger glyph from reading heavier than the 24px one it
 * replaces, and matches bidder's dialect.
 */
const ADMIN_V2_DIALECT: EmptyStateDialect = {
    root: 'ui-empty',
    glyph: 'ui-empty__glyph',
    icon: 'w-8 h-8',
    iconStrokeWidth: 1.5,
    title: 'ui-empty__title',
    message: 'ui-empty__body',
    titleAs: 'div',
    messageAs: 'div',
};

/**
 * The settings-v2 appearance for an empty state rendered INSIDE another card —
 * today, DataTableV2's empty slot (BL-1274).
 *
 * Identical to `ADMIN_V2_DIALECT` except for the root: `.ui-empty-inline` drops
 * the dashed border, the white fill, the 520px cap, and the auto margins. Those
 * are right for a block that sits alone on a page (Settings → Locations) and
 * wrong inside a table's own card, where they draw a second border around
 * nothing and cap the block at 520px in a much wider table.
 *
 * Every other slot is shared with `ADMIN_V2_DIALECT` on purpose: the glyph,
 * title, and body rules are flat selectors in settings-v2.css, so both roots
 * reuse them. That is what keeps this one dialect FAMILY with two container
 * treatments rather than a third fork — the distinction ADR 030 § 3b's Canon
 * draws between a deliberate dialect and an accidental duplicate.
 */
const ADMIN_V2_INLINE_DIALECT: EmptyStateDialect = {
    ...ADMIN_V2_DIALECT,
    root: 'ui-empty-inline',
};

/**
 * Props for the EmptyState component.
 */
export interface EmptyStateProps {
    /** Icon component to display (default: Inbox) */
    icon?: React.ElementType;
    /** Primary heading text */
    title: string;
    /** Optional description below the title (accepts rich content, e.g. an emphasized word) */
    message?: React.ReactNode;
    /**
     * Optional action button. `icon` renders leading the label — adopters that
     * hand-rolled their own empty state often had one (LocationsTable's "Add
     * Location" carried a `Plus`), so the shared component has to be able to
     * express it or migrating to it is a visual regression.
     *
     * `variant` picks the button's weight and defaults to `'primary'`, which is
     * what every pre-BL-1274 call site rendered. Pass `'secondary'` for a
     * corrective action rather than a creative one — BL-1274's "Clear filters"
     * would otherwise render as a full-weight primary competing with the
     * "Create auction" action on the sibling state.
     */
    action?: {
        label: string;
        onClick: () => void;
        icon?: React.ElementType;
        variant?: 'primary' | 'secondary';
    };
    /** Page shell this renders inside. Defaults to the classic Tailwind chrome. */
    variant?: EmptyStateVariant;
    /** Additional CSS classes */
    className?: string;
}

/**
 * Placeholder UI shown when a list, table, or section has no items.
 * @param props - Icon, title, message, optional action button and shell variant.
 * @returns A centered empty-state block in the requested dialect.
 */
export default function EmptyState({
    icon = Inbox,
    title,
    message,
    action,
    variant = 'classic',
    className,
}: EmptyStateProps) {
    // Both v2 dialects live inside the `.s-v2` shell and share its button
    // vocabulary; only the root container differs between them.
    const isV2 = variant === 'v2' || variant === 'v2-inline';

    // Admin's structured action is admin's button vocabulary, so it resolves
    // here rather than in the shared core: `.ui-btn--*` inside the `.s-v2`
    // token palette, the Tailwind <Button> everywhere else.
    const actionNode = action
        ? (isV2 ? (
            <button
                type="button"
                className={clsx('ui-btn', `ui-btn--${action.variant ?? 'primary'}`)}
                onClick={action.onClick}
            >
                {action.icon ? <action.icon className="w-4 h-4" aria-hidden /> : null}
                {action.label}
            </button>
        ) : (
            <Button variant="outline" size="sm" onClick={action.onClick} className="mt-4">
                {action.icon ? <action.icon className="w-4 h-4" aria-hidden /> : null}
                {action.label}
            </Button>
        ))
        : undefined;

    return (
        <UiEmptyState
            icon={icon}
            title={title}
            message={message}
            action={actionNode}
            dialect={
                variant === 'v2-inline'
                    ? ADMIN_V2_INLINE_DIALECT
                    : variant === 'v2'
                      ? ADMIN_V2_DIALECT
                      : ADMIN_CLASSIC_DIALECT
            }
            className={clsx(className)}
        />
    );
}
